import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Clock, FileText, Check, Lightbulb } from 'lucide-react';
import useTranslation from '../data/useTranslation';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TestIcons = {
  disc: '/picto-disc.png',
  bigfive: '/picto-bigfive.png',
  via: '/picto-via.png',
  enneagram: '/picto-enneagram.png',
  mbti: '/picto-mbti.png',
};

const SchwartzIcon = () => (
  <svg viewBox="0 0 64 64" className="w-14 h-14">
    <circle cx="32" cy="32" r="26" fill="none" stroke="#F59E0B" strokeWidth="2" opacity="0.3"/>
    <path d="M32 8 A24 24 0 0 1 56 32" fill="none" stroke="#EF4444" strokeWidth="4" strokeLinecap="round"/>
    <path d="M56 32 A24 24 0 0 1 32 56" fill="none" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round"/>
    <path d="M32 56 A24 24 0 0 1 8 32" fill="none" stroke="#22C55E" strokeWidth="4" strokeLinecap="round"/>
    <path d="M8 32 A24 24 0 0 1 32 8" fill="none" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="32" cy="32" r="10" fill="#F59E0B" opacity="0.2"/>
    <path d="M32 24 L34 30 L40 30 L35 34 L37 40 L32 36 L27 40 L29 34 L24 30 L30 30 Z" fill="#F59E0B"/>
  </svg>
);

const TEST_IDS = ['disc', 'bigfive', 'via', 'schwartz', 'enneagram', 'mbti'];
const TEST_META = {
  disc: { duration: 20, questions: 70, iconType: 'image' },
  bigfive: { duration: 15, questions: 50, iconType: 'image' },
  via: { duration: 20, questions: 120, iconType: 'image' },
  schwartz: { duration: 12, questions: 57, iconType: 'schwartz' },
  enneagram: { duration: 12, questions: 36, iconType: 'image' },
  mbti: { duration: 10, questions: 32, iconType: 'image' },
};

const TestCard = ({ testId, meta, tsp, isSelected, isCompleted, onToggle }) => {
  return (
    <div 
      className={`relative bg-[#1F2937] rounded-xl p-5 border-2 transition-all cursor-pointer ${
        isCompleted ? 'border-green-500/50 opacity-70' : isSelected ? 'border-orange-500 shadow-lg shadow-orange-500/20' : 'border-gray-700 hover:border-gray-600'
      }`}
      onClick={() => !isCompleted && onToggle(testId)}
      data-testid={`test-card-${testId}`}
    >
      <div className="absolute top-4 right-4">
        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
          isCompleted ? 'bg-green-500 border-green-500' : isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-500 bg-transparent'
        }`}>
          {(isSelected || isCompleted) && <Check className="w-4 h-4 text-white" />}
        </div>
      </div>
      
      <div className="mb-4">
        {meta.iconType === 'schwartz' ? <SchwartzIcon /> : (
          <img src={TestIcons[testId]} alt={testId} className="w-14 h-14 object-contain" />
        )}
      </div>
      
      <h3 className="text-xl font-bold text-white mb-1">{tsp[testId]?.subtitle ? testId.toUpperCase() === 'BIGFIVE' ? 'Big Five' : testId.toUpperCase() === 'VIA' ? 'VIA' : testId.charAt(0).toUpperCase() + testId.slice(1) : testId}</h3>
      <p className="text-orange-400 text-sm font-medium mb-3">{tsp[testId]?.subtitle}</p>
      <p className="text-gray-400 text-sm mb-4 line-clamp-3">{tsp[testId]?.description}</p>
      
      <div className="flex items-start gap-2 mb-4 p-3 bg-orange-500/10 rounded-lg">
        <Lightbulb className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
        <p className="text-orange-300 text-xs italic">{tsp[testId]?.insight}</p>
      </div>
      
      <div className="flex items-center justify-between text-gray-500 text-sm">
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>{meta.duration} {tsp.min}</span>
        </div>
        <div className="flex items-center gap-1">
          <FileText className="w-4 h-4" />
          <span>{meta.questions} questions</span>
        </div>
      </div>
      
      {isCompleted && (
        <div className="absolute inset-0 bg-green-500/10 rounded-xl flex items-center justify-center">
          <span className="bg-green-500 text-white px-4 py-2 rounded-full font-semibold text-sm">{tsp.completed}</span>
        </div>
      )}
    </div>
  );
};

export default function TestSelection() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tsp = t.testSelectionPage;
  const [assessment, setAssessment] = useState(null);
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Phase 2, pack-based test access (null = no auth context → show all)
  const [testsAuthorized, setTestsAuthorized] = useState(null);

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        let completedTests = [];
        try {
          const resultsRes = await fetch(`${API}/api/results/${assessmentId}`);
          if (resultsRes.ok) {
            const resultsData = await resultsRes.json();
            completedTests = resultsData.completed_tests || [];
          }
        } catch (e) {}

        const response = await fetch(`${API}/api/assessments/${assessmentId}`);
        if (!response.ok) throw new Error('Assessment not found');
        const data = await response.json();
        data.completed_tests = completedTests;
        setAssessment(data);

        // Phase 2, fetch user authorization to filter test selection
        const token = localStorage.getItem('bpm_access_token');
        if (token) {
          try {
            const dashRes = await fetch(`${API}/api/user/dashboard`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (dashRes.ok) {
              const dash = await dashRes.json();
              if (Array.isArray(dash.tests_authorized)) {
                setTestsAuthorized(dash.tests_authorized);
              }
            }
          } catch (e) { /* ignore, server still enforces */ }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (assessmentId) fetchAssessment();
    else { setError('Missing assessment ID'); setLoading(false); }
  }, [assessmentId]);
  
  const toggleTest = (testId) => {
    setSelectedTests(prev => prev.includes(testId) ? prev.filter(id => id !== testId) : [...prev, testId]);
  };
  
  const selectAll = () => {
    const completedTests = assessment?.completed_tests || [];
    setSelectedTests(
      TEST_IDS.filter(id =>
        !completedTests.includes(id) &&
        (!testsAuthorized || testsAuthorized.includes(id))
      )
    );
  };
  
  const startTests = () => {
    if (selectedTests.length === 0) return;
    navigate(`/assessment/${assessmentId}?tests=${selectedTests.join(',')}`);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t.common.loading}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-orange-500 text-white rounded-lg">{t.common.backToHome}</button>
        </div>
      </div>
    );
  }
  
  const completedTests = assessment?.completed_tests || [];
  const completedCount = completedTests.length;
  const selectedDuration = selectedTests.reduce((sum, testId) => sum + (TEST_META[testId]?.duration || 0), 0);
  
  return (
    <div className="min-h-screen bg-[#111827]">
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 py-8 px-6">
        <div className="container mx-auto max-w-6xl">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{tsp.heroTitle}</h1>
          <p className="text-orange-100 mb-6">{tsp.heroDesc}</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-4">
              <h3 className="text-orange-200 font-semibold mb-1">{tsp.card1Title}</h3>
              <p className="text-orange-100 text-sm">{tsp.card1Desc}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <h3 className="text-orange-200 font-semibold mb-1">{tsp.card2Title}</h3>
              <p className="text-orange-100 text-sm">{tsp.card2Desc}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <h3 className="text-orange-200 font-semibold mb-1">{tsp.card3Title}</h3>
              <p className="text-orange-100 text-sm">{tsp.card3Desc}</p>
            </div>
          </div>
        </div>
      </div>
      
      <main className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="bg-[#1F2937] rounded-xl p-4 mb-8 flex items-center justify-between">
          <span className="text-gray-400">{tsp.yourProgress}</span>
          <span className="text-orange-500 font-bold">{completedCount}/6 {tsp.tests}</span>
        </div>
        
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">{tsp.the6Dimensions}</h2>
          <button onClick={selectAll} className="px-4 py-2 bg-[#1F2937] hover:bg-gray-700 text-white rounded-lg transition-colors border border-gray-600" data-testid="select-all-tests">
            {tsp.selectAll}
          </button>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {TEST_IDS.filter(testId => !testsAuthorized || testsAuthorized.includes(testId)).map(testId => (
            <TestCard
              key={testId}
              testId={testId}
              meta={TEST_META[testId]}
              tsp={tsp}
              isSelected={selectedTests.includes(testId)}
              isCompleted={completedTests.includes(testId)}
              onToggle={toggleTest}
            />
          ))}
        </div>
        
        <div className="text-center">
          <button onClick={startTests} disabled={selectedTests.length === 0}
            className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
              selectedTests.length > 0 ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/30' : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`} data-testid="start-tests-btn">
            {selectedTests.length === 0 
              ? tsp.selectATest 
              : `${tsp.startTests} ${selectedTests.length} test${selectedTests.length > 1 ? 's' : ''} (${selectedDuration} ${tsp.min})`
            }
          </button>
        </div>
      </main>
    </div>
  );
}
